// Copyright (c) 2026 Michael F. Kamprath
//
// idle.js -- Idle animation loop and blink overlay for placed objects.
//
// After the entrance animation completes, each object enters an idle
// lifecycle:
//
//   1. BLINK: random-interval blink overlay (eyelids texture) that
//      fires independently of the idle animation state machine.
//
//   2. IDLE CYCLE (for themes that support it):
//      static wait → transitionToIdle → idleAnimation loop →
//      transitionFromIdle → repeat
//
// The blink overlay shows a separate eyelid-closed texture on top of
// the current sprite texture for 0.2s at random intervals (1.5-11.5s).
// During the idle animation pose, some themes have a different blink
// image (idleAnimationBlinkImage).
//
// The idle animation cycle is a sequence of simple frame-swap
// animations (no delta/scale transforms -- just image changes at
// timed intervals).  Flowers are special: they have an
// idleAnimationList (multiple sub-sequences played back-to-back)
// and mustCompleteLoop=true.

import * as PIXI from 'pixi.js';
import {
  setSpriteTexture,
  cycleNaturalDuration,
  animateOver,
} from './entrance.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get the inner Sprite from a container (duck-typed _inner field). */
function getInner(obj) {
  return obj._inner || obj;
}

/**
 * Resolve a keyframe image path to a PIXI Texture, using the pre-loaded
 * texture map when available, falling back to Assets.get.
 * textureMap can be a plain object or a Map.
 */
function resolveTexture(imagePath, textureMap) {
  if (!imagePath) return null;
  if (textureMap) {
    // Support both Map and plain-object texture maps
    const tex = (typeof textureMap.get === 'function')
      ? textureMap.get(imagePath)
      : textureMap[imagePath];
    if (tex) return tex;
  }
  const url = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return PIXI.Assets.get(url) || null;
}

/**
 * Random integer in [min, max] inclusive.
 */
function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

/**
 * Random blink interval in seconds: 1.5 + random(0-50)*0.2 = 1.5-11.5s.
 * Matches legacy CountableObjectView.randomBlinkTime.
 */
function randomBlinkInterval() {
  return 1.5 + randInt(0, 50) * 0.2;
}

/**
 * Random idle time as a fraction (25-100%) of the max seconds.
 * Matches legacy ramdomStaticIdleTime / randomAnimationIdleTime.
 */
function randomIdleTime(maxSeconds) {
  const pct = 0.25 + Math.random() * 0.75; // 25% to 100%
  return maxSeconds * pct;
}

// ---------------------------------------------------------------------------
// Frame-sequence player
// ---------------------------------------------------------------------------

/**
 * Play a simple frame-swap sequence on a sprite.  No delta/scale/rotation
 * transforms -- purely image changes at the timed keyframe points.
 *
 * @param {object}   obj        The sprite container (duck-typed _inner)
 * @param {Array}    keyframes  Array of { t, image, lastFrameDuration }
 * @param {Map}      textureMap Pre-loaded textures
 * @param {object}   cancel     { cancelled: boolean } token
 * @returns {Promise<void>}
 */
function playFrameSequence(obj, keyframes, textureMap, cancel) {
  const duration = cycleNaturalDuration(keyframes);
  const durationMs = duration * 1000;

  return animateOver(durationMs, (_norm, elapsedMs) => {
    const t = elapsedMs / 1000;
    // Find the current frame: last keyframe whose t <= current time
    let currentFrame = keyframes[0];
    for (let i = 1; i < keyframes.length; i++) {
      if (keyframes[i].t <= t) {
        currentFrame = keyframes[i];
      } else {
        break;
      }
    }
    if (currentFrame.image) {
      const tex = resolveTexture(currentFrame.image, textureMap);
      if (tex) setSpriteTexture(obj, tex);
    }
  }, cancel);
}

// Expose for unit tests only.
export { playFrameSequence as __playFrameSequenceForTests };
export { createBlinkController as __createBlinkControllerForTests };

// ---------------------------------------------------------------------------
// Blink controller
// ---------------------------------------------------------------------------

/**
 * Create a blink overlay controller for a single object.
 *
 * The blink overlay is a child Sprite added on top of the inner sprite
 * inside the object container.  It shows the eyelid-closed texture for
 * 0.2s then hides again.
 *
 * @param {object} obj           The sprite container
 * @param {object} blinkConfig   { staticBlinkImage, idleBlinkImage? }
 * @param {Map}    textureMap    Pre-loaded textures
 * @returns {{ start(), stop(), setIdlePose(boolean), destroy() }}
 */
function createBlinkController(obj, blinkConfig, textureMap) {
  const inner = getInner(obj);

  // Create the blink overlay sprite as a child of the inner sprite's
  // parent (the object container), positioned on top of the inner sprite.
  const staticBlinkTex = resolveTexture(blinkConfig.staticBlinkImage, textureMap);
  if (!staticBlinkTex) {
    // No blink texture available -- return a no-op controller.
    return { start() {}, stop() {}, setIdlePose() {}, destroy() {} };
  }

  const idleBlinkTex = blinkConfig.idleBlinkImage
    ? resolveTexture(blinkConfig.idleBlinkImage, textureMap)
    : null;

  // Add the blink sprite as a child of the INNER sprite rather than the
  // container.  As a child it inherits the inner sprite's transform
  // (position, scale, flip, rotation) automatically, so the eyelids
  // track the eyes regardless of entrance animation state.
  //
  // The inner sprite has anchor (0.5, 0.5), so its local origin is its
  // visual center.  A child at (0, 0) with the same anchor sits on top
  // of it perfectly — but we need the blink sprite's own scale to be 1:1
  // relative to the texture, not relative to the inner sprite's computed
  // scale.  We set the blink sprite dimensions in texture pixels and let
  // PIXI's parent–child scale stacking handle the rest.
  const blinkSprite = new PIXI.Sprite(staticBlinkTex);
  blinkSprite.anchor.set(0.5, 0.5);
  blinkSprite.x = 0;
  blinkSprite.y = 0;
  // Scale 1:1 relative to the inner sprite.  Both textures are the
  // same native pixel size, so scale (1, 1) makes them overlay exactly.
  blinkSprite.scale.set(1, 1);
  blinkSprite.visible = false;

  // Add on top of the inner sprite's content.
  inner.addChild(blinkSprite);

  let blinkTimer = null;
  let blinkHideTimer = null;
  let isInIdlePose = false;
  let stopped = false;

  function doBlink() {
    if (stopped) return;
    // Choose the right blink texture based on current pose
    const tex = (isInIdlePose && idleBlinkTex) ? idleBlinkTex : staticBlinkTex;
    blinkSprite.texture = tex;
    blinkSprite.visible = true;

    blinkHideTimer = setTimeout(() => {
      blinkSprite.visible = false;
      scheduleNextBlink();
    }, 200); // 0.2s blink duration
  }

  function scheduleNextBlink() {
    if (stopped) return;
    const intervalMs = randomBlinkInterval() * 1000;
    blinkTimer = setTimeout(doBlink, intervalMs);
  }

  return {
    start() {
      stopped = false;
      scheduleNextBlink();
    },
    stop() {
      stopped = true;
      if (blinkTimer) { clearTimeout(blinkTimer); blinkTimer = null; }
      if (blinkHideTimer) { clearTimeout(blinkHideTimer); blinkHideTimer = null; }
      blinkSprite.visible = false;
    },
    setIdlePose(inIdle) {
      isInIdlePose = inIdle;
    },
    destroy() {
      this.stop();
      if (blinkSprite.parent) {
        blinkSprite.parent.removeChild(blinkSprite);
      }
      blinkSprite.destroy();
    },
  };
}

// ---------------------------------------------------------------------------
// Idle animation controller (per-object)
// ---------------------------------------------------------------------------

/**
 * States for the idle animation state machine.
 */
const IdleState = {
  STATIC: 'static',
  TRANSITION_TO_IDLE: 'transitionToIdle',
  IDLE_ANIMATION: 'idleAnimation',
  TRANSITION_FROM_IDLE: 'transitionFromIdle',
};

/**
 * Create an idle animation controller for a single object.
 *
 * @param {object} obj           The sprite container
 * @param {object} idleConfig    The theme's idle config from animations.json
 * @param {Map}    textureMap    Pre-loaded textures
 * @param {object} blinkCtrl     The blink controller (to coordinate idle pose)
 * @returns {{ start(), stop(), destroy() }}
 */
function createIdleController(obj, idleConfig, textureMap, blinkCtrl) {
  // No idle animation for this theme (e.g. apples)
  if (!idleConfig || idleConfig.supportsIdleAnimation === false) {
    return { start() {}, stop() {}, destroy() {} };
  }

  const maxStaticSec = idleConfig.maxStaticIdleSeconds || 20;
  const maxAnimSec = idleConfig.maxAnimationIdleSeconds || 10;
  const mustComplete = idleConfig.mustCompleteLoop || false;

  // For themes with idleAnimationList (flowers), each entry is a
  // sub-sequence.  For others, there's a single idleAnimation array.
  const hasAnimList = Array.isArray(idleConfig.idleAnimationList);

  let state = IdleState.STATIC;
  let stopped = false;
  let stateTimer = null;
  let cancelToken = { cancelled: false };

  function cancelCurrentAnimation() {
    cancelToken.cancelled = true;
    cancelToken = { cancelled: false };
  }

  /**
   * Run one full idle cycle: static wait → transition to idle →
   * idle animation loop → transition from idle → repeat.
   */
  async function runIdleCycle() {
    if (stopped) return;

    // --- STATIC WAIT ---
    state = IdleState.STATIC;
    blinkCtrl.setIdlePose(false);
    blinkCtrl.start();
    const staticWait = randomIdleTime(maxStaticSec);
    await waitSeconds(staticWait);
    if (stopped) return;

    // --- TRANSITION TO IDLE ---
    // Stop blinking during transitions and idle animation so the
    // eyelid overlay doesn't flash on top of a different pose.
    blinkCtrl.stop();
    if (idleConfig.transitionToIdle) {
      state = IdleState.TRANSITION_TO_IDLE;
      await playFrameSequence(obj, idleConfig.transitionToIdle, textureMap, cancelToken);
      if (stopped) return;
    }

    // --- IDLE ANIMATION LOOP ---
    state = IdleState.IDLE_ANIMATION;
    blinkCtrl.setIdlePose(true);

    if (hasAnimList) {
      // Flower-style: play sub-sequences back-to-back in a loop.
      // mustCompleteLoop means we always finish the full sequence list
      // before checking if we should stop.
      const animDuration = randomIdleTime(maxAnimSec);
      const startTime = performance.now();
      let listIdx = 0;

      while (!stopped) {
        const elapsed = (performance.now() - startTime) / 1000;
        if (elapsed >= animDuration && !mustComplete) break;
        // If mustComplete, finish the current complete pass through
        // the list before checking time
        if (elapsed >= animDuration && mustComplete && listIdx === 0) break;

        await playFrameSequence(
          obj,
          idleConfig.idleAnimationList[listIdx],
          textureMap,
          cancelToken,
        );
        if (stopped) return;
        listIdx = (listIdx + 1) % idleConfig.idleAnimationList.length;
      }
    } else if (idleConfig.idleAnimation) {
      // Standard: loop the single idle animation for the random duration.
      const animDuration = randomIdleTime(maxAnimSec);
      const startTime = performance.now();

      while (!stopped) {
        const elapsed = (performance.now() - startTime) / 1000;
        if (elapsed >= animDuration) break;

        await playFrameSequence(obj, idleConfig.idleAnimation, textureMap, cancelToken);
        if (stopped) return;
      }
    }

    if (stopped) return;
    blinkCtrl.setIdlePose(false);

    // --- TRANSITION FROM IDLE ---
    if (idleConfig.transitionFromIdle) {
      state = IdleState.TRANSITION_FROM_IDLE;
      await playFrameSequence(obj, idleConfig.transitionFromIdle, textureMap, cancelToken);
      if (stopped) return;
    }

    // Restore static texture
    const staticTex = obj._staticTexture;
    if (staticTex) setSpriteTexture(obj, staticTex);

    // Recurse to start the next cycle
    if (!stopped) {
      runIdleCycle();
    }
  }

  function waitSeconds(sec) {
    return new Promise((resolve) => {
      if (stopped) { resolve(); return; }
      stateTimer = setTimeout(() => {
        stateTimer = null;
        resolve();
      }, sec * 1000);
    });
  }

  return {
    start() {
      stopped = false;
      cancelToken = { cancelled: false };
      runIdleCycle();
    },
    stop() {
      stopped = true;
      cancelCurrentAnimation();
      if (stateTimer) { clearTimeout(stateTimer); stateTimer = null; }
      // Restore static texture when stopping
      const staticTex = obj._staticTexture;
      if (staticTex) setSpriteTexture(obj, staticTex);
    },
    destroy() {
      this.stop();
    },
  };
}

// ---------------------------------------------------------------------------
// Scene-level idle manager
// ---------------------------------------------------------------------------

/**
 * Collect all image paths referenced by idle animations so they can be
 * preloaded before the idle system starts.
 *
 * @param {object} idleConfig  The theme's idle config from animations.json
 * @returns {string[]}  Array of image paths (relative to public/)
 */
export function collectIdleImagePaths(idleConfig) {
  if (!idleConfig || idleConfig.supportsIdleAnimation === false) return [];

  const paths = new Set();

  function addFromKeyframes(keyframes) {
    if (!keyframes) return;
    for (const k of keyframes) {
      if (k.image) paths.add(k.image);
    }
  }

  if (idleConfig.idleAnimationStillImage) {
    paths.add(idleConfig.idleAnimationStillImage);
  }
  if (idleConfig.idleAnimationBlinkImage) {
    paths.add(idleConfig.idleAnimationBlinkImage);
  }

  addFromKeyframes(idleConfig.transitionToIdle);
  addFromKeyframes(idleConfig.transitionFromIdle);
  addFromKeyframes(idleConfig.idleAnimation);

  if (Array.isArray(idleConfig.idleAnimationList)) {
    for (const seq of idleConfig.idleAnimationList) {
      addFromKeyframes(seq);
    }
  }

  return [...paths];
}

/**
 * Preload all textures needed for idle animations + blink overlays.
 * Returns a plain object map { path: Texture } matching the format
 * used by preloadCycleTextures in entrance.js.
 *
 * @param {object} animConfig  The theme's animations config
 * @param {object} themeData   The theme data (for blink image paths)
 * @returns {Promise<Object>}  { path: Texture }
 */
export async function preloadIdleTextures(animConfig, themeData) {
  const idleConfig = animConfig?.idle;
  const paths = new Set();

  // Blink images from theme
  if (themeData?.sprites?.blink) paths.add(themeData.sprites.blink);

  // Idle-specific images
  for (const p of collectIdleImagePaths(idleConfig)) {
    paths.add(p);
  }

  const map = {};
  const { Assets } = PIXI;
  for (const p of paths) {
    try {
      const alias = '/' + p;
      // Cache-aware: skip Assets.add when already registered to
      // avoid the "already has key: X overwriting" warning on repeat
      // preloads (variant swaps reuse the same idle textures).
      const cached = Assets && typeof Assets.get === 'function'
        ? Assets.get(alias) : null;
      if (cached) { map[p] = cached; continue; }
      if (Assets && typeof Assets.add === 'function') Assets.add({ alias, src: alias });
      if (Assets && typeof Assets.load === 'function') {
        map[p] = await Assets.load(alias);
      }
    } catch {
      /* best effort */
    }
  }
  return map;
}

/**
 * Create a scene-level idle + blink manager.  Call start() after the
 * entrance animation completes.
 *
 * @param {object}   opts
 * @param {Array}    opts.sprites     Array of object containers
 * @param {object}   opts.animConfig  The theme's animations config
 * @param {object}   opts.themeData   The theme data (for blink image paths)
 * @param {Map}      opts.textureMap  Pre-loaded textures
 * @returns {{ start(), stop(), destroy() }}
 */
export function createIdleManager({ sprites, animConfig, themeData, textureMap }) {
  const idleConfig = animConfig?.idle;
  const hasIdleAnim = idleConfig && idleConfig.supportsIdleAnimation !== false
    && (idleConfig.idleAnimation || idleConfig.idleAnimationList);

  // Build per-object blink + idle controllers
  const controllers = sprites.map((obj) => {
    // Blink config: static blink from theme, idle blink from animations
    const blinkConfig = {
      staticBlinkImage: themeData?.sprites?.blink || null,
      idleBlinkImage: idleConfig?.idleAnimationBlinkImage || null,
    };

    const blinkCtrl = blinkConfig.staticBlinkImage
      ? createBlinkController(obj, blinkConfig, textureMap)
      : { start() {}, stop() {}, setIdlePose() {}, destroy() {} };

    const idleCtrl = createIdleController(obj, idleConfig, textureMap, blinkCtrl);

    return { blink: blinkCtrl, idle: idleCtrl };
  });

  let started = false;

  return {
    start() {
      started = true;
      for (const c of controllers) {
        // Stagger the initial start slightly per object so they
        // don't all blink/transition in sync.
        const staggerMs = Math.random() * 2000;
        setTimeout(() => {
          if (!started) return; // manager was stopped during stagger
          if (hasIdleAnim) {
            // The idle controller manages blink start/stop:
            // blink runs during static wait, pauses during
            // transitions and idle animation.
            c.idle.start();
          } else {
            // No idle animation (e.g. apples) -- just blink.
            c.blink.start();
          }
        }, staggerMs);
      }
    },
    stop() {
      started = false;
      for (const c of controllers) {
        c.blink.stop();
        c.idle.stop();
      }
    },
    destroy() {
      started = false;
      for (const c of controllers) {
        c.blink.destroy();
        c.idle.destroy();
      }
    },
    /**
     * Pause idle + blink for a single sprite (by index).  Used by the
     * tap reaction system to freeze just the tapped object while its
     * touch animation plays.
     *
     * @param {number} index  Position in the sprites array
     */
    pauseSprite(index) {
      const c = controllers[index];
      if (!c) return;
      c.blink.stop();
      c.idle.stop();
    },
    /**
     * Resume idle + blink for a single sprite after a tap reaction
     * completes.  No-op if the manager itself has been stopped.
     *
     * @param {number} index  Position in the sprites array
     */
    resumeSprite(index) {
      if (!started) return;   // manager-level stop overrides per-sprite
      const c = controllers[index];
      if (!c) return;
      if (hasIdleAnim) {
        c.idle.start();
      } else {
        c.blink.start();
      }
    },
  };
}
